import React, { useState } from "react";
import { authApi } from "@shared/api/auth.api";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label
} from "@shared/ui/components";

interface EmailRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
}

export const EmailRequestModal: React.FC<EmailRequestModalProps> = ({ open, onClose, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!email.trim()) {
      setError("Введите email");
      return;
    }

    if (!validateEmail(email)) {
      setError("Некорректный формат email");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authApi.updateEmail(email);
      onSuccess(email);
      handleClose();
    } catch (err: any) {
      console.error("[EMAIL_UPDATE] Error:", err);
      setError(err.response?.data?.message || err.message || "Ошибка при сохранении email");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (): void => {
    setEmail("");
    setError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" && !isLoading) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Укажите email для оплаты</DialogTitle>
          <DialogDescription>
            Для проведения платежа необходимо указать email. На него будут приходить чеки об оплате.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              autoFocus
            />
          </div>

          {error && <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !email.trim()}>
            {isLoading ? "Сохранение..." : "Продолжить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
